import Settings from '@/settings';
import rawLocalData from './raw';
import { fixAssetPathsDeep } from '@/assets/scripts/asset-path';

type LangCode = 'C' | 'E';

const ok = { Succeeded: true, Success: true, Code: 0, Message: '', ReturnValue: true };
const emptyPage = { Data: [], TotalRecord: 0, TotalPage: 0, ReturnValue: { Data: [], TotalRecord: 0, TotalPage: 0 } };
const contact = {
  code: 1,
  data: {
    company: 'New Century International Insurance Advisory Limited',
    phone: '852 3106 0515',
    fax: '852 3106 0516',
    email: 'cs@ncihk.com',
    whatsappUrl: 'https://api.whatsapp.com/send?phone=85261329147&text=%E6%88%91%E6%83%B3%E6%9F%A5%E8%A9%A2%E4%B8%80%E4%B8%8B',
    facebook: 'https://www.facebook.com/profile.php?id=61568681746197',
    address: '香港九龍長沙灣道833號長沙灣廣場一期5樓502A室',
    addressEn: 'Room 502A, 5/F, Cheung Sha Wan Plaza Tower 1, 833 Cheung Sha Wan Road, Kowloon, Hong Kong',
    googleMap: '<p><iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3690.4372524704454!2d114.14615207586719!3d22.33711294155908!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x340407548be66e61%3A0xc6bc9dedfdffc038!2z6ZW_5rKZ5rm-5bm_5Zy6!5e0!3m2!1szh-CN!2shk!4v1767584496915!5m2!1szh-CN!2shk" width="100%" height="100%" frameborder="0"></iframe></p>'
  }
};

function currentLang(): LangCode {
  let lang = Settings.defaultLang;
  if (typeof localStorage !== 'undefined') lang = localStorage.getItem('locale') || lang;
  return lang === 'E' ? 'E' : 'C';
}

function dataRoot(lang?: string) {
  return rawLocalData[(lang === 'E' ? 'E' : currentLang()) as LangCode] || rawLocalData.C;
}

function normalizePath(url: string) {
  let path = url || '';
  path = path.replace(/^https?:\/\/[^/]+/i, '');
  path = path.replace(/^\/V\d+/i, '');
  return path.split('?')[0].toLowerCase();
}

function stable(value: any): string {
  if (!value || typeof value !== 'object') return JSON.stringify(value || {});
  if (Array.isArray(value)) return JSON.stringify(value.map(stableValue));
  return JSON.stringify(stableValue(value));
}

function stableValue(value: any): any {
  if (!value || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(stableValue);
  const output = {};
  Object.keys(value).sort().forEach((key) => { output[key] = stableValue(value[key]); });
  return output;
}

function first(bucket: any, fallback: any = null) {
  if (!bucket) return fallback;
  const keys = Object.keys(bucket);
  return keys.length ? bucket[keys[0]] : fallback;
}

function pick(bucket: any, params: any, fallback: any = null, debugLabel: string = '') {
  if (!bucket) {
    debugPick(debugLabel, params, 'missing bucket', fallback);
    return fallback;
  }
  const direct = bucket[JSON.stringify(params || {})];
  if (direct !== undefined) {
    debugPick(debugLabel, params, 'direct', direct);
    return direct;
  }
  const sorted = bucket[stable(params || {})];
  if (sorted !== undefined) {
    debugPick(debugLabel, params, 'stable', sorted);
    return sorted;
  }
  const keys = Object.keys(bucket);
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    try {
      const parsed = JSON.parse(key);
      if (matches(parsed, params || {})) {
        debugPick(debugLabel, params, 'partial match: ' + key, bucket[key]);
        return bucket[key];
      }
    } catch (e) {}
  }
  const result = fallback !== null ? fallback : first(bucket, fallback);
  debugPick(debugLabel, params, 'fallback', result);
  return result;
}

function debugPick(debugLabel: string, params: any, matchType: string, result: any) {
}

function matches(source: any, target: any) {
  if (!source || !target) return false;
  const targetKeys = Object.keys(target);
  return targetKeys.every((key) => String(source[key]) === String(target[key]));
}

function listFrom(result: any) {
  if (!result) return [];
  if (Array.isArray(result)) return result;
  if (Array.isArray(result.Data)) return result.Data;
  if (result.ReturnValue && Array.isArray(result.ReturnValue.Data)) return result.ReturnValue.Data;
  if (result.ReturnValue && Array.isArray(result.ReturnValue.Contents)) return result.ReturnValue.Contents;
  if (Array.isArray(result.Catalogs)) return result.Catalogs;
  return [];
}

function cmsResult(result: any) {
  if (!result) {
    return { Succeeded: true, ReturnValue: {}, CMS: {}, Code: 0 };
  }
  if (!result.ReturnValue) {
    return { ...result, ReturnValue: {}, CMS: result.CMS || {} };
  }
  const cms = result.ReturnValue.CMS || result.CMS || result.ReturnValue;
  const normalized = { ...result, CMS: cms, ReturnValue: { ...cms, CMS: cms } };
  return normalized;
}

function response(data: any) {
  return Promise.resolve({ data: fixAssetPathsDeep(data) });
}

function nciContent(root: any, params: any) {
  const isMobile = params.isMobile === true || params.isMobile === 'true' || params.isMobile === 1 || params.isMobile === '1';
  const homeResult = pick(root.cms.contentByDevice, { Key: 'HomeAbout', IsMobile: isMobile }, null);
  const aboutResult = pick(root.cms.contentByDevice, { Key: '20346', ContentId: '20346', IsMobile: isMobile }, null);
  const homeAbout = homeResult && (homeResult.CMS || homeResult.ReturnValue) ? (homeResult.CMS || homeResult.ReturnValue) : {};
  const aboutPage = aboutResult && (aboutResult.CMS || aboutResult.ReturnValue) ? (aboutResult.CMS || aboutResult.ReturnValue) : {};

  return { code: 1, data: { homeAbout, aboutPage } };
}

function saveLocalLang(lang: string) {
  if (typeof localStorage !== 'undefined') localStorage.setItem('locale', lang);
  return ok;
}

function localMember(path: string, params: any) {
  if (path.indexOf('/member/saveuilang') === 0) return saveLocalLang(params.lang || params.Lang || 'C');
  if (path.indexOf('/member/setcurrency') === 0) return { ...ok, Currency: { Id: params.currencyId || 1, Code: 'HK' } };
  if (path.indexOf('/member/getprofile') === 0) return { Succeeded: false, ReturnValue: null, Message: '' };
  if (path.indexOf('/member/getaccount') === 0) return { Succeeded: false, ReturnValue: null, Message: '' };
  if (path.indexOf('/member/login') === 0) return { Succeed: false, Succeeded: false, ReturnValue: null, Message: '' };
  if (path.indexOf('/member/logout') === 0) return ok;
  return { ...ok, ReturnValue: null, Data: [] };
}

function localShopCart() {
  return { ...ok, Data: [], ReturnValue: null };
}

function localOrder(path: string) {
  if (path.indexOf('/order/getorder') === 0) return emptyPage;
  if (path.indexOf('/defaultorder/createdefaultorder') === 0) return { ...ok, ReturnValue: { Id: 'LOCAL_ORDER', OrderNo: 'LOCAL_ORDER' } };
  return ok;
}

function localRegAndPay(path: string) {
  if (path.indexOf('/regnpay/genform') === 0) return { Succeeded: true, ReturnValue: '', Html: '' };
  if (path.indexOf('/regnpay/get') === 0) return { Succeeded: true, ReturnValue: null };
  if (path.indexOf('/regnpay/getsubmitcomplete') === 0) return { Succeeded: true, ReturnValue: null };
  return ok;
}

export function resolveLocalRequest(method: string, url: string, options: any = {}) {
  const path = normalizePath(url);
  const params = method.toLowerCase() === 'get' ? (options.params || {}) : (options.data || {});
  const root = dataRoot(params.lang || params.Lang);

  if (path.indexOf('/member/') === 0) return response(localMember(path, params));
  if (path.indexOf('/shoppingcart') === 0) return response(localShopCart());
  if (path.indexOf('/order/') === 0 || path.indexOf('/defaultorder/') === 0 || path.indexOf('/promotion/') === 0) return response(localOrder(path));
  if (path.indexOf('/message/') === 0 || path.indexOf('/points/') === 0) return response(emptyPage);
  if (path.indexOf('/productcomment/') === 0) return response(emptyPage);
  if (path.indexOf('/regnpay/') === 0) return response(localRegAndPay(path));

  switch (path) {
    case '/banner/getpromotionlist':
      return response(pick(root.promotion.promotionList, params, first(root.promotion.headerBanners, [])));
    case '/banner/getpromotion':
      return response(pick(root.promotion.promotions, params, { Succeeded: true, ReturnValue: null, Promotion: null }));
    case '/seo/gethomeseodata':
      return response(root.promotion.homeSeoData || {});
    case '/menu':
      return response(root.promotion.menu || []);

    case '/cms/getcontent':
      return response(pick(root.cms.content, params, first(root.cms.content, null)));
    case '/cms/getcontentbydevice':
      return response(cmsResult(pick(root.cms.contentByDevice, params, first(root.cms.contentByDevice, { Succeeded: true, ReturnValue: null, CMS: null }), 'contentByDevice')));
    case '/cms/getcontentbykey':
      return response(pick(root.cms.contentByKey, params, first(root.cms.contentByKey, null)));
    case '/cms/getcontentsbycatkey':
      return response(pick(root.cms.contentsByCatKey, params, first(root.cms.contentsByCatKey, emptyPage)));
    case '/cms/getcontentsbycatkeyex':
      return response(pick(root.cms.contentsByCatKeyEx, params, first(root.cms.contentsByCatKeyEx, emptyPage)));
    case '/cms/getcontentsbycatid':
      return response(pick(root.cms.contentsByCatId, params, first(root.cms.contentsByCatId, emptyPage)));
    case '/cms/getcontents':
      return response(pick(root.cms.contents, params, emptyPage));
    case '/cms/getcategorytree':
      return response(pick(root.cms.categoryTree, params, first(root.cms.categoryTree, [])));
    case '/cms/getnextcategorytree':
      return response(pick(root.cms.nextCategoryTree, params, first(root.cms.categoryTree, [])));
    case '/cms/getsubcatcontents':
      return response(pick(root.cms.subCatContents, params, first(root.cms.subCatContents, emptyPage)));
    case '/cms/getlastestcontents':
      return response(pick(root.cms.lastestContents, params, emptyPage));
    case '/cms/getcategoryrelatecontent':
      return response(pick(root.cms.categoryRelateContent, params, emptyPage));
    case '/cms/getcontentsbytitle':
      return response(pick(root.cms.contentsByTitle, params, first(root.cms.contentsByTitle, emptyPage)));
    case '/cms/getcategory':
      return response(pick(root.cms.category, params, first(root.cms.category, null)));
    case '/cms/getcategorybydevice':
      return response(pick(root.cms.categoryByDevice, params, first(root.cms.categoryByDevice, emptyPage)));
    case '/cms/getcatbykey':
      return response(pick(root.cms.catByKey, params, first(root.cms.catByKey, null)));

    case '/product':
      return response(pick(root.product.products, params, first(root.product.products, null)));
    case '/product/searchv2':
      return response(pick(root.product.search, params, first(root.product.search, emptyPage)));
    case '/product/getcatprodbypage':
      return response(pick(root.product.catProducts, params, first(root.product.catProducts, emptyPage)));
    case '/product/getrelatedproduct':
      return response(pick(root.product.related, params, first(root.product.related, { YouWouldLike: [] })));
    case '/product/getlatest':
      return response(pick(root.product.latest, params, first(root.product.latest, [])));
    case '/catalog/getcatalogs':
      return response(root.product.catalogs || []);
    case '/catalog/getcatalog':
      return response(pick(root.product.catalog, params, first(root.product.catalog, null)));
    case '/product/getattrimage':
      return response(pick(root.product.attrImage, params, ''));
    case '/product/getproductupordown':
      return response(pick(root.product.productUpOrDown, params, 0));
    case '/product/checkproductstatus':
      return response(pick(root.product.checkProductStatus, params, { ReturnValue: 1 }));
    case '/memberrecommendproduct/save':
      return response(ok);

    case '/common/contact':
      return response(root.site.contact && !root.site.contact.__fetchError ? root.site.contact : contact);
    case '/api/common/ncicontent':
    case '/common/ncicontent':
      return response(nciContent(root, params));
    case '/common/contactsubmit':
      return response(ok);
    case '/photoalbum/searchfolders':
      return response(root.photoAlbum.folders || []);
    case '/photoalbum/searchfolderinfo':
      return response(pick(root.photoAlbum.folderInfo, params, first(root.photoAlbum.folderInfo, [])));
    case '/calenderevent/getlistbycond':
      return response(pick(root.calendar.listByCond, params, first(root.calendar.listByCond, [])));
    case '/calenderevent/getbyid':
      return response(pick(root.calendar.byId, params, first(root.calendar.byId, null)));

    case '/delivery/country':
      return response(root.common.country || []);
    case '/delivery/getcountyforex':
      return response(root.common.country || []);
    case '/delivery/province':
    case '/delivery/getprovinceforex':
    case '/delivery/city':
      return response([]);
    case '/delivery/getpickupaddress':
      return response(root.common.pickupAddress || []);
    case '/delivery/getpickuppointcharge':
    case '/express/getexpresspointwithdiscount':
    case '/express/getexpresspointcharge':
    case '/express/getdefaultexpresschargeforex':
      return response([]);
    case '/delivery/getshunfengpointtype':
      return response([]);
    case '/express/getexpresstimerange':
      return response([]);
    case '/express/getexpressandoutlets':
      return response(root.common.expressAndOutlets || []);
    case '/pay/savepickupinfo':
      return response(ok);
    case '/pay/getpaymentmethod':
      return response(root.common.paymentMethod || []);
    case '/pay/savepaymethod':
    case '/pay/saveofflinepay':
      return response(ok);
    case '/address/getaddresses':
    case '/address/getaddressesforex':
      return response([]);
    case '/address/getdefaultaddr':
    case '/address/getdefaultaddrforex':
      return response(null);
    case '/address/createaddress':
    case '/address/updateaddress':
    case '/address/removeaddress':
      return response(ok);
    case '/prodattr/getattrs':
    case '/prodattr/getattribtuebyid':
      return response(pick(root.common.attrList, params, first(root.common.attrList, [])));
    case '/inboundrule/getareacodebyip':
      return response({ AreaCode: 'HK', Lang: currentLang() });
    case '/ipaddress/getipaddressinfo':
      return response({ AreaCode: 'HK', Lang: currentLang() });
    case '/fileupload/uploadfile':
      return response('');
    default:
      return response(emptyPage);
  }
}

export function localList(result: any) {
  return listFrom(result);
}
